import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getLocalstorageItem,
  localStorageKeys,
  setLocalstorageItem,
} from '@/helpers/localStorage';

const keyCurrentDevice = localStorageKeys.monitoring + '.currentDevice';
const keyMonitoringEnabled = localStorageKeys.monitoring + '.enabled';
import { useAppStore } from '@/store/store';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/store/user';

export const useMonitoringGeneralStore = defineStore('monitoringGeneralStore', () => {
  const userStore = useUserStore();

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();

  const appStore = useAppStore();

  const loading = ref(false);
  const currentDevice = ref(getLocalstorageItem(keyCurrentDevice));

  const devices = computed(() => userStore.user?.devices ?? []);

  const currentDeviceIntegrationIsFlexio = computed(() => isFlexioDevice(currentDevice.value));
  const monitoringEnabled = computed(() => (getLocalstorageItem(keyMonitoringEnabled) || devices.value.length > 0) && appStore.feature('monitoring'));

  const config = computed(() => ({
    home: {
      label: t('monitoring.home'),
      color: 'monitoringHome',
      chart: {
        fill: true,
      },
      order: 100,
    },
    ownConsumption: {
      label: t('monitoring.ownConsumption'),
      color: 'monitoringHome',
      chart: {
        fill: true,
      },
      order: 0,
    },
    grid: {
      label: t('monitoring.grid'),
      color: 'monitoringGrid',
      icon: 'mdi-transmission-tower',
      chart: {
        fill: true,
      },
    },
    ev: {
      label: t('monitoring.ev'),
      color: 'monitoringEv',
      icon: 'mdi-ev-station',
      chart: {
        fill: true,
      },
    },
    solar: {
      label: t('monitoring.solar'),
      color: 'monitoringSolar',
      icon: 'mdi-solar-power-variant-outline',
      chart: {
        fill: true,
      },
      order: 0,
    },
    battery: {
      label: t('monitoring.battery'),
      color: 'monitoringBattery',
      icon: 'mdi-battery-medium',
      chart: {
        fill: true,
      },
    },
    batteryCharge: {
      label: t('monitoring.charge'),
      color: 'monitoringCharge',
      order: 50,
    },
    batteryDischarge: {
      label: t('monitoring.discharge'),
      color: 'monitoringDischarge',
      order: 50,
    },
    batteryChargeState: {
      label: t('monitoring.batterySoc'),
      color: 'monitoringSOC',
    },
    gridConsume: {
      label: t('monitoring.consume'),
      color: 'monitoringConsume',
      order: 51,
    },
    gridInject: {
      label: t('monitoring.inject'),
      color: 'monitoringInject',
      order: 51,
    },

    priceConsumption: {
      label: t('monitoring.priceConsumption'),
      color: 'monitoringPriceConsumption',
      icon: 'mdi-transmission-tower-export',
      chart: {
        yAxisID: 'y1',
        fill: false,
        borderDash: [3, 3],
      },
    },
    priceInjection: {
      label: t('monitoring.priceInjection'),
      color: 'monitoringPriceInjection',
      icon: 'mdi-transmission-tower-import',
      chart: {
        yAxisID: 'y1',
        fill: false,
        borderDash: [3, 3],
      },
    },

    insights: {
      earnings: {
        label: t('monitoring.earnings'),
        color: 'monitoringEarnings',
        icon: 'mdi-home-analytics',
      },

      savings: {
        label: t('monitoring.savings'),
        color: 'monitoringSavings',
        icon: 'mdi-scale-balance',

        solar: {
          label: t('monitoring.solar'),
          color: 'monitoringSolarControl',
        },
        battery: {
          label: t('monitoring.batteryControl'),
          color: 'monitoringBatteryControl',
        },
        tariff: {
          label: t('monitoring.tariff'),
          color: 'monitoringTariffChoice',
        },
        ev: {
          label: t('monitoring.ev'),
          color: 'monitoringEvControl',
        },
        energy: {
          label: t('monitoring.energy'),
          color: 'monitoringEnergyControl',
        },
      },
    },
  }));

  const currentDeviceType = computed(() => currentDevice.value?.monitoringIntegration);
  const currentDeviceId = computed(() => currentDevice.value?.deviceId);

  const selectDevice = async (device) => {
    if (['insights', 'ev'].includes(route.name) && !isFlexioDevice(device)) {
      await router.push({ name: 'monitoring' });
    }

    currentDevice.value = device;
    setLocalstorageItem(keyCurrentDevice, device);
  };

  const selectDeviceById = (deviceId) => {
    const device = devices.value.find((device) => device.deviceId === deviceId);

    if (device) {
      selectDevice(device);
    }
  };

  const isFlexioDevice = (device) => device?.monitoringIntegration === 'flexio';

  watch(devices, (val) => {
    selectDevice(val[0]);
  });

  return {
    loading,
    monitoringEnabled,
    currentDeviceIntegrationIsFlexio,
    devices,
    currentDevice,
    currentDeviceId,
    currentDeviceType,
    config,
    selectDevice,
    selectDeviceById,
  };
});
